import { useState, useCallback, useRef } from "react";
import { ChatClient, ChatRequest, ChatResponse } from "../store/repsaApi";
import axiosClient, { app_uri } from "../utils/axiosFactory";
import { processConversation } from "../store/modules/chat/chatActions";

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  error?: boolean;
}

export interface ChatSession {
  sessionId?: string;
  messageCount?: number;
  dailyMessageCount?: number;
  dailyLimit?: number;
  isLimitReached?: boolean;
}

export interface UseChatOptions {
  initialMessage?: string;
  sessionId?: string;
  onError?: (error: Error) => void;
  onLimitReached?: (session: ChatSession) => void;
}

export const useChat = (options: UseChatOptions = {}) => {
  const {
    initialMessage = "Hi! I'm your data assistant. Ask me anything about your transactions, clients, or financial data.",
    sessionId: initialSessionId,
    onError,
    onLimitReached,
  } = options;

  const [sessionInfo, setSessionInfo] = useState<ChatSession>({
    sessionId: initialSessionId,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      text: initialMessage,
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const addMessage = useCallback(
    (message: Omit<ChatMessage, "id" | "timestamp">) => {
      const newMessage: ChatMessage = {
        ...message,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    []
  );

  const updateMessage = useCallback(
    (id: string, updates: Partial<ChatMessage>) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg))
      );
    },
    []
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      // Check if user has reached daily limit
      if (sessionInfo.isLimitReached) {
        onLimitReached?.(sessionInfo);
        return;
      }

      // Add user message
      addMessage({
        text,
        isUser: true,
      });

      // Add loading message
      const loadingMessage = addMessage({
        text: "Thinking...",
        isUser: false,
        isLoading: true,
      });

      setIsLoading(true);

      try {
        const response: ChatResponse = await processConversation(
          text,
          sessionInfo.sessionId
        );

        // Update session info with response data
        setSessionInfo({
          sessionId: response.sessionId,
          messageCount: response.messageCount,
          dailyMessageCount: response.dailyMessageCount,
          dailyLimit: response.dailyLimit,
          isLimitReached: response.isLimitReached,
        });

        // Update the loading message with the response
        updateMessage(loadingMessage.id, {
          text: response.answer || "Sorry, I couldn't generate a response.",
          isLoading: false,
        });

        // Check if limit was reached after this message
        if (response.isLimitReached) {
          onLimitReached?.({
            sessionId: response.sessionId,
            messageCount: response.messageCount,
            dailyMessageCount: response.dailyMessageCount,
            dailyLimit: response.dailyLimit,
            isLimitReached: response.isLimitReached,
          });
        }
      } catch (error) {
        console.error("Error in conversation:", error);

        // Check if it's an ApiException with a response message
        let errorMessage = "An unexpected error occurred";
        if (
          error &&
          typeof error === "object" &&
          "isApiException" in error &&
          error.isApiException
        ) {
          errorMessage =
            (error as any).response || (error as any).message || errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        updateMessage(loadingMessage.id, {
          text: `Sorry, I encountered an error: ${errorMessage}`,
          isLoading: false,
          error: true,
        });

        onError?.(error instanceof Error ? error : new Error(errorMessage));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, sessionInfo, addMessage, updateMessage, onError, onLimitReached]
  );

  const clearChat = useCallback(() => {
    setMessages([
      {
        id: "1",
        text: initialMessage,
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    // Reset session to start a new conversation
    setSessionInfo({
      sessionId: undefined,
    });
  }, [initialMessage]);

  const getSessionInfo = useCallback(() => sessionInfo, [sessionInfo]);

  return {
    messages,
    isLoading,
    sendMessage,
    clearChat,
    addMessage,
    updateMessage,
    sessionInfo,
    getSessionInfo,
  };
};
